/**
 * "You're still in class" background reminders.
 *
 * When a student leaves the tab (Home, app switch, another tab) the class keeps
 * running — but nothing on their phone says so. This does three cheap things
 * that need no permission gymnastics:
 *   1. Pulses the tab title so the tab itself screams "live class".
 *   2. Fires a single system notification after a grace period (only if the
 *      student already granted permission).
 *   3. Clears both the moment they come back.
 */

let titleTimer = null;
let awayTimer = null;
let originalTitle = "";
let notif = null;
let active = false;

const PULSE = ["\uD83D\uDD34 Live class — Udyog", "Tap to return — you're still in class"];

function startPulse() {
  if (titleTimer) return;
  let i = 0;
  originalTitle = originalTitle || document.title;
  document.title = PULSE[0];
  titleTimer = setInterval(() => {
    i = (i + 1) % PULSE.length;
    document.title = PULSE[i];
  }, 1600);
}

function stopPulse() {
  if (titleTimer) {
    clearInterval(titleTimer);
    titleTimer = null;
  }
  if (originalTitle) document.title = originalTitle;
}

function showNotification(title) {
  try {
    if (!("Notification" in window) || Notification.permission !== "granted") return;
    notif = new Notification("Your class is still running", {
      body: title ? `${title} — tap to go back in` : "Tap to go back into the class",
      tag: "udyog-class-live",
      icon: "/icon-192.png",
      badge: "/icon-192.png",
      silent: false,
      requireInteraction: false,
    });
    notif.onclick = () => {
      window.focus();
      notif?.close();
      notif = null;
    };
  } catch (_) {}
}

function clearNotification() {
  try {
    notif?.close();
  } catch (_) {}
  notif = null;
}

/** Ask for notification permission — call inside a user gesture. */
export async function askNotificationPermission() {
  try {
    if (!("Notification" in window)) return "unsupported";
    if (Notification.permission !== "default") return Notification.permission;
    return await Notification.requestPermission();
  } catch (_) {
    return "denied";
  }
}

export function startStillHere(meetingTitle = "", onReturn) {
  if (active) return;
  active = true;
  originalTitle = document.title;
  let awaySince = 0;
  const onVisibility = () => {
    if (document.visibilityState === "hidden") {
      awaySince = Date.now();
      startPulse();
      clearTimeout(awayTimer);
      awayTimer = setTimeout(() => showNotification(meetingTitle), 10000);
    } else {
      clearTimeout(awayTimer);
      stopPulse();
      clearNotification();
      const secs = awaySince ? Math.round((Date.now() - awaySince) / 1000) : 0;
      awaySince = 0;
      if (secs >= 8) onReturn?.(secs);
    }
  };
  document.addEventListener("visibilitychange", onVisibility);
  stopStillHere._cleanup = () => {
    document.removeEventListener("visibilitychange", onVisibility);
    clearTimeout(awayTimer);
    stopPulse();
    clearNotification();
  };
}

export function stopStillHere() {
  if (!active) return;
  active = false;
  stopStillHere._cleanup?.();
  stopStillHere._cleanup = null;
}
